import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/**
 * 登入閘門。**未登入時擋下動作，就地開對話框，登入完再把那件事做完。**
 *
 * 原本一律導到 `/login?redirect=…`：使用者按了「存款」，整個畫面被換掉，
 * 登入後才被送回來。中間他離開了原本在看的東西，而且如果決定不登入，
 * 還得自己按返回。對「點一款遊戲」這種隨手的動作，代價尤其不成比例。
 *
 * 狀態放在**模組層級**而不是 pinia：這裡沒有任何需要被別處讀取或持久化的
 * 資料，只有「現在擋著什麼」這一個瞬時值。路由守衛也要能寫它 ——
 * 守衛不在元件的 setup 裡，拿不到 `useX()` 的回傳值，但 import 得到模組。
 *
 * 對話框本身掛在 `layouts/GlobalGates.vue`，全站只有一份。
 */

/** 擋下來的原因。只影響對話框裡那一句說明文字 */
export type AuthGateReason =
  'game' | 'deposit' | 'withdraw' | 'transactions' | 'bets' | 'account' | 'generic'

/** 路由名稱 → 說明文字的原因。名字對得上就直接用，對不上退到通用那句 */
const ROUTE_REASONS = new Set<string>([
  'game',
  'deposit',
  'withdraw',
  'transactions',
  'bets',
  'account',
])

/**
 * 被擋下的那件事。
 *
 * 分成兩種是因為來源不同：路由守衛手上只有一個目的地（它自己不能導航，
 * 那會在守衛裡遞迴），而 `launch()` 這類呼叫端手上是一個 closure。
 *
 * 路由那一種存的是 `fullPath` 字串而非 `RouteLocationRaw`：`resume()` 要能
 * 拿它跟「現在在哪」比對，物件形式比不了。
 */
type GateIntent = { kind: 'route'; to: string } | { kind: 'action'; run: () => void }

interface Pending {
  reason: AuthGateReason
  intent: GateIntent
}

const pending = ref<Pending | null>(null)

/**
 * 從元件外（路由守衛）擋下一次導航。
 *
 * 導出成獨立函式而不是掛在 `useAuthGate()` 上：守衛沒有 setup context，
 * 呼叫 `useRouter()` 會拿到 `undefined`。
 */
export function requestAuthForRoute(reason: AuthGateReason, fullPath: string): void {
  pending.value = { reason, intent: { kind: 'route', to: fullPath } }
}

export function gateReasonForRoute(name: string): AuthGateReason {
  return ROUTE_REASONS.has(name) ? (name as AuthGateReason) : 'generic'
}

/**
 * 關掉閘門並丟掉被擋下的那件事。
 *
 * 與 `requestAuthForRoute` 同理導出成獨立函式：狀態是模組層級的，
 * 測試之間不歸零就會互相污染。
 */
export function closeAuthGate(): void {
  pending.value = null
}

export function useAuthGate() {
  const router = useRouter()
  const auth = useAuthStore()

  const isOpen = computed(() => pending.value !== null)
  const reason = computed<AuthGateReason>(() => pending.value?.reason ?? 'generic')

  /**
   * 要登入才能做的事都包一層這個。
   *
   * 已登入就直接做 —— 呼叫端不需要自己判斷，也就不會有人漏判。
   */
  function requireAuth(why: AuthGateReason, run: () => void): void {
    if (auth.isAuthenticated) {
      run()
      return
    }

    pending.value = { reason: why, intent: { kind: 'action', run } }
  }

  /** 使用者放棄登入。被擋下的那件事直接丟掉，不留到下一次 */
  const close = closeAuthGate

  /**
   * 登入成功，把當初被擋下的那件事做完。
   *
   * 先清 `pending` 再執行：`run()` 有可能又打開另一個對話框
   * （點遊戲 → 登入 → 進遊戲確認），先清掉才不會被這裡覆蓋回去。
   */
  function resume(): void {
    const intent = pending.value?.intent ?? null
    pending.value = null

    if (intent === null) return

    if (intent.kind === 'action') {
      intent.run()
      return
    }

    // session 中途失效時擋下的是「使用者現在所在的這一頁」（見 `main.ts`），
    // 那時候不必也不能再導一次 —— 導到同一個位址會被 router 判為重複導航
    if (intent.to !== router.currentRoute.value.fullPath) void router.push(intent.to)
  }

  return { isOpen, reason, requireAuth, close, resume }
}
